import * as Y from '../../../../shared/yjs';
import { CommandContext, Commands, ReadResult, PatchLogger, CommandExecutor, Insert, Remove, Transform, Replace } from './internal/yDocHighLevelAPI';
import { ReplaceWithMarkdown } from './internal/yDocLLMAPI';
import { yDocumentService, generateLLMId } from './internal/yDocLowLevelAPI';
import { getDocContext as resolveDoc } from './internal/yDocLLMAPI';
import { toMarkdown as mdToMarkdown, nodesToMarkdown as mdNodesToMarkdown } from './markdown';

/**
 * Public DocContext shape exposed by the facade.
 * Wraps the Y.Doc and the ProseMirror Y.XmlFragment along with identifiers.
 */
export type DocContext = {
  yDoc: Y.Doc;
  yxml: Y.XmlFragment;
  docId: string;
  windowId?: string;
  metadata?: CommandContext['metadata'];
};

/**
 * Public helper: generate a stable node id.
 * All node IDs should use this, and they will always start with "node_".
 */
export function generateNodeId(): string {
  return generateLLMId('node');
}

/**
 * Resolve an LLM-facing id to a concrete Y.Doc context.
 * Returns null doc with error if the document is not available.
 * 
 * ✅ SIMPLIFIED: Virtual Y.js creation for PDFs/Word removed
 * PDFs and Word docs now use SimpleNodeProvider (direct markdown conversion)
 */
export async function getDocContext(llmId: string, ctx: any): Promise<{ doc: DocContext | null; error?: string }>{
  console.log('[getDocContext] Resolving document:', llmId);
  
  // Resolve Y.js document (live docs only)
  const { yDoc, docContext, docId, windowId, error } = await resolveDoc(llmId, ctx);
  if (!error && yDoc && docId && docContext) {
    console.log('[getDocContext] ✅ Document resolution successful');
    return { doc: { yDoc, yxml: (docContext as any).yxml, docId, windowId, metadata: (docContext as any).metadata } } as any;
  }

  console.error('[getDocContext] Document resolution failed for:', llmId, error);
  return { doc: null, error: error || 'document not available' };
}

/** Convert public DocContext to internal CommandContext expected by engines. */
function asCommandContext(doc: DocContext): CommandContext {
  return {
    yxml: doc.yxml,
    doc: doc.yDoc,
    docId: doc.docId,
    metadata: doc.metadata,
  } as unknown as CommandContext;
}

/** Read the entire document into a lightweight JSON representation. */
export function readAll(docContext: CommandContext): ReadResult {
  return Commands.read.all(docContext);
}

/** Read a contiguous range (inclusive) by node ids. */
export function readRange(docContext: CommandContext, fromId: string, toId: string): ReadResult {
  return Commands.read.range(docContext, fromId, toId);
}

/** Full-text search over the lightweight JSON view. */
export function search(docContext: CommandContext, query: string, limit: number = 50): ReadResult {
  return Commands.read.search(docContext, query, { limit });
}

/** Compute document stats (counts, size estimates). */
export function stats(docContext: CommandContext) {
  return Commands.read.stats(docContext);
}

/**
 * Replace a span of nodes with Markdown content. If target is { all: true },
 * the span is resolved to cover the whole document.
 */
export function replaceWithMarkdown(
  ctx: CommandContext | DocContext,
  target: { from: string; to: string } | { all: true },
  markdown: string,
  opts?: { revisionId?: string; batchDescription?: string; suggestionMode?: boolean; restrictIds?: string[] }
) {
  const docContext: CommandContext = (ctx as any).yxml ? (ctx as CommandContext) : asCommandContext(ctx as DocContext);
  
  let resolvedTarget = target as any;
  if ('all' in target && target.all) {
    const all = Commands.read.all(docContext);
    if (!all.nodes || all.nodes.length === 0) {
      return { ok: false, error: 'empty document' } as any;
    }
    resolvedTarget = { from: all.nodes[0].id, to: all.nodes[all.nodes.length - 1].id };
  }
  
  const executor = new CommandExecutor(docContext);
  const cmd = new ReplaceWithMarkdown(resolvedTarget, markdown, opts);

  let success: boolean;
  try {
    console.log('🚀 [EXECUTE_START] About to execute ReplaceWithMarkdown command');
    console.log('🚀 [EXECUTE_CONTEXT] Command details:', {
      targetType: 'replaceNodes' in (opts || {}) ? 'specific_nodes' : 'span',
      targetValue: 'all' in target ? { all: true } : { from: (target as any).from, to: (target as any).to },
      markdownLength: markdown.length,
      timestamp: new Date().toISOString()
    });
    
    success = executor.execute(cmd);
    
    console.log('✅ [EXECUTE_SUCCESS] ReplaceWithMarkdown executed successfully, success=', success);
    console.log('✅ [EXECUTE_POST] Command state after execution:', {
      success,
      hasRevisionId: !!cmd.getRevisionId?.(),
      revisionId: cmd.getRevisionId?.(),
      hasInsertedNodeIds: !!(cmd as any).insertedNodeIds,
      insertedNodeIdsLength: (cmd as any).insertedNodeIds?.length || 0,
      timestamp: new Date().toISOString()
    });
  } catch (executionError) {
    console.error('❌ [EXECUTION_ERROR] Execute threw exception:', {
      error: executionError,
      message: (executionError as any)?.message,
      stack: (executionError as any)?.stack?.split('\n').slice(0, 5), // First 5 lines of stack
      timestamp: new Date().toISOString()
    });
    console.error('❌ [EXECUTION_ERROR_STATE] Command state on error:', {
      commandType: cmd.constructor.name,
      hasRevisionId: !!cmd.getRevisionId?.(),
      hasGetInsertedNodeIds: typeof cmd.getInsertedNodeIds === 'function',
      timestamp: new Date().toISOString()
    });
    success = false;
  }

  const result = success
    ? { ok: true, revisionId: (cmd as any).getRevisionId?.(), insertedIds: success ? (cmd as any).getInsertedNodeIds?.() || [] : [] }
    : { ok: false, error: 'execution_failed' };

  if (success) {
    const ids = (result as any).insertedIds || [];
    console.log('📊 [SAFE_ID_EXTRACTION] docApi.replaceWithMarkdown() - Success! Got', ids.length, 'IDs from command:', ids);
  } else {
    console.error('❌ [SAFE_ID_EXTRACTION] docApi.replaceWithMarkdown() - Command failed!');
  }

  console.log('📤 [REPLACEMARKDOWN_RETURN] Returning result:', {
    ok: result.ok,
    error: (result as any).error,
    revisionIdPresent: !!result.revisionId,
    insertedIdsCount: (result as any).insertedIds?.length || 0,
    timestamp: new Date().toISOString()
  });

  return result;
}

/** Get pending patches recorded by PatchLogger. */
export function getPendingPatches(ctx: CommandContext | DocContext) {
  const docContext: CommandContext = (ctx as any).yxml ? (ctx as CommandContext) : asCommandContext(ctx as DocContext);
  return PatchLogger.getPendingPatches(docContext);
}

/** Accept a patch and perform the physical content mutation. */
export function acceptPatch(ctx: CommandContext | DocContext, patchId: string): boolean {
  const docContext: CommandContext = (ctx as any).yxml ? (ctx as CommandContext) : asCommandContext(ctx as DocContext);
  return PatchLogger.acceptPatch(docContext, patchId);
}

/** Reject a patch and revert/remove the corresponding content. */
export function rejectPatch(ctx: CommandContext | DocContext, patchId: string): boolean {
  const docContext: CommandContext = (ctx as any).yxml ? (ctx as CommandContext) : asCommandContext(ctx as DocContext);
  return PatchLogger.rejectPatch(docContext, patchId);
}

// Facade mutation wrappers
export function insert(
  ctx: CommandContext | DocContext,
  content: any[],
  location: any,
  options?: any
) {
  const docContext: CommandContext = (ctx as any).yxml ? (ctx as CommandContext) : asCommandContext(ctx as DocContext);
  const executor = new CommandExecutor(docContext);
  const cmd = new Insert(content, location, options);
  const success = executor.execute(cmd);
  
  if (success) {
    const nodeIds = (cmd as any).getInsertedNodeIds?.() || [];
    console.log('📊 [SAFE_ID_EXTRACTION] docApi.insert() - Success! Got', nodeIds.length, 'node IDs from command:', nodeIds);
    return { ok: success, insertedNodes: nodeIds };
  } else {
    console.error('❌ [SAFE_ID_EXTRACTION] docApi.insert() - Command failed!');
    return { ok: success, insertedNodes: [] };
  }
}

export function remove(
  ctx: CommandContext | DocContext,
  target: any,
  options?: any
) {
  const docContext: CommandContext = (ctx as any).yxml ? (ctx as CommandContext) : asCommandContext(ctx as DocContext);
  const executor = new CommandExecutor(docContext);
  const cmd = new Remove(target, options);
  const success = executor.execute(cmd);
  return { ok: success };
}

export function transform(
  ctx: CommandContext | DocContext,
  target: any,
  transformation: any,
  options?: any
) {
  const docContext: CommandContext = (ctx as any).yxml ? (ctx as CommandContext) : asCommandContext(ctx as DocContext);
  const executor = new CommandExecutor(docContext);
  const cmd = new Transform(target, transformation, options);
  const success = executor.execute(cmd);
  return { ok: success };
}

// JSON-based replace wrapper (ProseMirror-style content)
export function replace(
  ctx: CommandContext | DocContext,
  target: { from: string; to: string } | { all: true },
  content: any[],
  options?: { revisionId?: string; batchDescription?: string; suggestionMode?: boolean; restrictIds?: string[] }
) {
  const docContext: CommandContext = (ctx as any).yxml ? (ctx as CommandContext) : asCommandContext(ctx as DocContext);
  let resolvedTarget = target as any;
  if ('all' in target && target.all) {
    const all = Commands.read.all(docContext);
    if (!all.nodes || all.nodes.length === 0) {
      return { ok: false, error: 'empty document' } as any;
    }
    resolvedTarget = { from: all.nodes[0].id, to: all.nodes[all.nodes.length - 1].id };
  }
  const executor = new CommandExecutor(docContext);
  // Replace expects (target, content, options)
  const cmd = new Replace(resolvedTarget, content, options);
  const success = executor.execute(cmd);
  if (success) {
    const ids = (cmd as any).getInsertedNodeIds?.() || [];
    console.log('📊 [SAFE_ID_EXTRACTION] docApi.replace() - Success! Got', ids.length, 'IDs from command:', ids);
    const result = { ok: true, revisionId: (cmd as any).getRevisionId?.(), insertedIds: ids };
    return result;
  } else {
    console.error('❌ [SAFE_ID_EXTRACTION] docApi.replace() - Command failed!');
    const result = { ok: false, error: 'execution_failed' };
    return result;
  }
}

/**
 * Resolve a span of node ids with configurable contiguity behavior.
 * - If "ids" provided: 
 *   - contiguous: true (default) → verifies all exist under same parent and returns full [min,max] id range
 *   - contiguous: false → returns only the exact specified node IDs (no range expansion)
 * - Else if fromId/toId provided: verifies same parent and returns inclusive range.
 * - Else if anchors provided: uses read-all nodes to locate start/end by text and returns range.
 */
export function resolveSpan(
  ctx: CommandContext | DocContext,
  params: {
    ids?: string[];
    fromId?: string;
    toId?: string;
    anchors?: { startText?: string; endText?: string };
    contiguous?: boolean; // Default: true for backwards compatibility
  }
): { rangeIds: string[] } | null {
  const docContext: CommandContext = (ctx as any).yxml ? (ctx as CommandContext) : asCommandContext(ctx as DocContext);

  const collectIdsFromParentRange = (parent: any, minIndex: number, maxIndex: number): string[] => {
    const rangeIds: string[] = [];
    for (let i = minIndex; i <= maxIndex; i++) {
      const el = (parent as any).get(i);
      if (el instanceof Y.XmlElement) {
        const idAttr = el.getAttribute('id');
        if (idAttr) rangeIds.push(idAttr);
      }
    }
    return rangeIds;
  };

  // 1) ids
  if (params.ids && Array.isArray(params.ids) && params.ids.length > 0) {
    const contiguous = params.contiguous !== false; // Default to true for backwards compatibility
    
    console.log('🔍 [RESOLVE_SPAN_DEBUG] Processing node IDs:', {
      nodeIds: params.ids,
      contiguous,
      mode: contiguous ? 'range_expansion' : 'exact_nodes_only'
    });
    
    const locations = params.ids
      .map((nodeId) => ({ nodeId, loc: yDocumentService.findNodeById(docContext.doc, nodeId as any, { strict: true }) }))
      .filter((x) => !!x.loc);
      
    if (locations.length === 0) {
      console.warn('🔍 [RESOLVE_SPAN_DEBUG] No valid node locations found:', {
        requestedIds: params.ids,
        foundLocations: 0
      });
      return null;
    }
    
    if (locations.length !== params.ids.length) {
      const missingIds = params.ids.filter(id => !locations.some(loc => loc.nodeId === id));
      console.warn('🔍 [RESOLVE_SPAN_DEBUG] Some nodes not found:', {
        requestedIds: params.ids,
        foundCount: locations.length,
        missingIds
      });
    }
    
    const parentRef = locations[0]!.loc!.parent;
    const crossParent = locations.some((x) => x.loc!.parent !== parentRef);
    
    if (crossParent) {
      console.warn('🔍 [RESOLVE_SPAN_DEBUG] Cross-parent nodes detected:', {
        nodeIds: params.ids,
        contiguous,
        reason: 'nodes_span_multiple_parents'
      });
      return null;
    }
    
    if (!contiguous) {
      // NON-CONTIGUOUS MODE: Return only the exact specified node IDs
      const exactIds = locations.map(loc => loc.nodeId);
      console.log('✅ [RESOLVE_SPAN_DEBUG] Non-contiguous mode - returning exact nodes:', {
        requestedIds: params.ids,
        returnedIds: exactIds,
        mode: 'exact_nodes_only'
      });
      return { rangeIds: exactIds };
    } else {
      // CONTIGUOUS MODE: Original behavior - expand to full range
      const indices = locations.map((x) => x.loc!.index);
      const minIndex = Math.min(...indices);
      const maxIndex = Math.max(...indices);
      const rangeIds = collectIdsFromParentRange(parentRef, minIndex, maxIndex);
      
      console.log('✅ [RESOLVE_SPAN_DEBUG] Contiguous mode - range expansion:', {
        requestedIds: params.ids,
        minIndex,
        maxIndex,
        expandedToIds: rangeIds,
        mode: 'range_expansion',
        expansionRatio: `${params.ids.length} → ${rangeIds.length}`
      });
      
      return { rangeIds };
    }
  }

  // 2) explicit bounds
  if (params.fromId && params.toId) {
    const start = yDocumentService.findNodeById(docContext.doc, params.fromId as any, { strict: true });
    const end = yDocumentService.findNodeById(docContext.doc, params.toId as any, { strict: true });
    if (!start || !end) return null;
    if (start.parent !== end.parent) return null;
    const minIndex = Math.min(start.index, end.index);
    const maxIndex = Math.max(start.index, end.index);
    return { rangeIds: collectIdsFromParentRange(start.parent, minIndex, maxIndex) };
  }

  // 3) anchors by text
  if (params.anchors && (params.anchors.startText || params.anchors.endText)) {
    const all = Commands.read.all(docContext);
    const nodes = all.nodes || [];
    const findByText = (needle?: string) =>
      typeof needle === 'string' && needle.length > 0
        ? nodes.find((n: any) => typeof n.text === 'string' && n.text.includes(needle))
        : undefined;
    const startNode = findByText(params.anchors.startText) || nodes[0];
    const endNode = findByText(params.anchors.endText) || nodes[nodes.length - 1];
    const fromId = startNode?.id;
    const toId = endNode?.id;
    if (!fromId || !toId) return null;
    return resolveSpan(docContext, { fromId, toId });
  }

  return null;
}


/**
 * Markdown helpers exposed via the facade so callers only depend on docApi.
 */
export function toMarkdown(ctx: CommandContext | DocContext | Y.Doc): string {
  let ydoc: Y.Doc | null = null;
  if (ctx instanceof Y.Doc) {
    ydoc = ctx as Y.Doc;
  } else if ((ctx as any)?.yDoc) {
    ydoc = (ctx as DocContext).yDoc;
  } else if ((ctx as any)?.doc) {
    ydoc = (ctx as CommandContext).doc as unknown as Y.Doc;
  }
  if (!ydoc) return '';
  return mdToMarkdown(ydoc);
}

export function nodesToMarkdown(ctx: CommandContext | DocContext, nodeIds: string[]): string {
  const docContext: CommandContext = (ctx as any).yxml ? (ctx as CommandContext) : asCommandContext(ctx as DocContext);
  return mdNodesToMarkdown(docContext, nodeIds);
}


